import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material';
import { getActiveTheme } from '../data/themeApi';
import { getTheme, mapThemeName, getAvailableThemes, type ThemeName } from '../themes';

export type { ThemeName };

type ThemeContextType = {
  currentTheme: ThemeName;
  setCurrentTheme: (theme: ThemeName) => void;
  availableThemes: ThemeName[];
  themeDetails: {
    id: number;
    name: string;
    active: boolean;
  } | null;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<ThemeName>('default');
  const [themeDetails, setThemeDetails] = useState<{
    id: number;
    name: string;
    active: boolean;
  } | null>(null);

  // Fetch active theme on initial load
  useEffect(() => {
    const fetchActiveTheme = async () => {
      try {
        const theme = await getActiveTheme();
        // Convert the theme name from the API to a valid theme name
        const themeName = mapThemeName(theme.name);
        setCurrentTheme(themeName);
        setThemeDetails({
          id: theme.id,
          name: theme.name,
          active: theme.active,
        });
      } catch (error) {
        console.error('Failed to fetch active theme:', error);
        // Fallback to default theme
        setCurrentTheme('default');
      }
    };

    fetchActiveTheme();
  }, []);

  // Apply theme class to body
  useEffect(() => {
    document.body.classList.remove('default-theme', 'rusticCottage-theme', 'adventurePassion-theme');
    document.body.classList.add(`${currentTheme}-theme`);
  }, [currentTheme]);



  const availableThemes = getAvailableThemes();

  const value = {
    currentTheme,
    setCurrentTheme,
    availableThemes,
    themeDetails,
  };

  // Get the appropriate theme object based on the current theme name
  const themeObject = getTheme(currentTheme);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={themeObject}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 